import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Camera,
  FileText,
  MapPin,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  Download,
  X,
  Loader2,
  AlertCircle,
  Container,
  Ship,
} from "lucide-react";


export default function InspectionViewPage() {
  const { inspectionId, token } = useParams();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    fetchInspection();
  }, [inspectionId, token]);

  const fetchInspection = async () => {
    try {
      const response = await axios.get(`${API_URL}/inspection/${inspectionId}/${token}`);
      setInspection(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Inspection not found or link has expired");
    } finally {
      setLoading(false);
    }
  };

  const photoSrc = (photo) => {
    if (!photo) return "";
    if (photo.url) return `${API_BASE_URL}${photo.url}`;
    if (photo.grid_id) return `${API_BASE_URL}/api/inspection-photo/${photo.grid_id}`;
    return `data:${photo.content_type};base64,${photo.data}`;
  };

  const downloadPhoto = (photo) => {
    const link = document.createElement('a');
    link.href = photoSrc(photo);
    link.download = photo.filename || 'inspection_photo.jpg';
    link.click();
  };

  const downloadPdf = () => {
    window.open(`${API_URL}/inspection/${inspectionId}/${token}/pdf`, '_blank');
  };

  const openPhotoViewer = (index) => {
    setCurrentPhotoIndex(index);
    setSelectedPhoto(inspection.photos[index]);
  };

  const navigatePhoto = (direction) => {
    const newIndex = currentPhotoIndex + direction;
    if (newIndex >= 0 && newIndex < inspection.photos.length) {
      setCurrentPhotoIndex(newIndex);
      setSelectedPhoto(inspection.photos[newIndex]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading inspection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Inspection Not Found</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  // Photo viewer overlay
  if (selectedPhoto) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 text-white bg-black/50">
          <button onClick={() => setSelectedPhoto(null)} className="p-2">
            <X className="w-6 h-6" />
          </button>
          <span className="font-medium">
            {currentPhotoIndex + 1} / {inspection.photos.length}
          </span>
          <button onClick={() => downloadPhoto(selectedPhoto)} className="p-2">
            <Download className="w-6 h-6" />
          </button>
        </div>

        {/* Image */}
        <div className="flex-1 flex items-center justify-center p-4 relative">
          {currentPhotoIndex > 0 && (
            <button
              onClick={() => navigatePhoto(-1)}
              className="absolute left-4 p-3 bg-white/20 rounded-full hover:bg-white/30"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          
          <img
            src={photoSrc(selectedPhoto)}
            alt={selectedPhoto.filename}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
          
          {currentPhotoIndex < inspection.photos.length - 1 && (
            <button
              onClick={() => navigatePhoto(1)}
              className="absolute right-4 p-3 bg-white/20 rounded-full hover:bg-white/30"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}
        </div>

        {/* Filename */}
        <div className="p-4 text-center text-white text-sm bg-black/50">
          {selectedPhoto.filename}
        </div>
      </div>
    );
  }

  const getInspectionTypeLabel = (type) => {
    const labels = {
      physical_check: "Customs Physical Check",
      customs_physical: "Customs Physical Check",
      document_check: "Document Check",
      seal_check: "Seal Verification",
      cargo_damage: "Cargo Damage Inspection",
      veterinary: "Veterinary Inspection",
      phytosanitary: "Phytosanitary Inspection",
    };
    return labels[type] || type?.replace(/_/g, ' ');
  };

  const getInspectionColor = (type) => {
    const colors = {
      physical_check: "#dc2626",
      customs_physical: "#dc2626",
      cargo_damage: "#ea580c",
      veterinary: "#16a34a",
      phytosanitary: "#65a30d",
      document_check: "#2563eb",
      seal_check: "#7c3aed",
    };
    return colors[type] || "#3b82f6";
  };

  const inspectionColor = getInspectionColor(inspection?.inspection_type);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header style={{ background: `linear-gradient(135deg, ${inspectionColor}, ${inspectionColor}dd)` }} className="text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
            <Camera className="w-4 h-4" />
            <span>{getInspectionTypeLabel(inspection?.inspection_type)} Report</span>
          </div>
          <h1 className="text-2xl font-bold">
            {inspection.pf_number || inspection.container_number || inspection.bl_number}
          </h1>
          {inspection.result && (
            <div className="mt-2">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                inspection.result === "pass" ? "bg-green-500 text-white" :
                inspection.result === "fail" ? "bg-red-500 text-white" :
                inspection.result === "pending" ? "bg-amber-500 text-white" :
                inspection.result === "conditional" ? "bg-amber-500 text-white" :
                "bg-white/20 text-white"
              }`}>
                {inspection.result === "pass" ? "✓ PASSED" :
                 inspection.result === "fail" ? "✗ FAILED" :
                 inspection.result === "pending" ? "⏳ PENDING — final report to follow" :
                 inspection.result === "conditional" ? "⚠ CONDITIONAL" :
                 inspection.result?.toUpperCase()}
              </span>
            </div>
          )}
          <button
            onClick={downloadPdf}
            className="mt-4 inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 transition-colors text-white text-sm font-medium px-4 py-2 rounded-lg backdrop-blur"
            data-testid="download-report-pdf-btn"
          >
            <Download className="w-4 h-4" />
            Download PDF report
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Info Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {inspection.pf_number && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Ship className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">PF Number</p>
                  <p className="font-semibold text-slate-800">{inspection.pf_number}</p>
                </div>
              </div>
            )}
            
            {inspection.container_number && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Container className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Container</p>
                  <p className="font-semibold text-slate-800 font-mono">{inspection.container_number}</p>
                </div>
              </div>
            )}
            
            {inspection.bl_number && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">B/L Number</p>
                  <p className="font-semibold text-slate-800">{inspection.bl_number}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Camera className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Inspection Type</p>
                <p className="font-semibold text-slate-800">
                  {getInspectionTypeLabel(inspection.inspection_type)}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="my-5" />

          {/* Details */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Inspector</p>
                <p className="text-slate-800">{inspection.inspector_name || "—"}</p>
              </div>
            </div>

            {inspection.staff_member && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Staff member</p>
                  <p className="text-slate-800">{inspection.staff_member}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Date &amp; Time</p>
                <p className="text-slate-800">
                  {(() => {
                    const d = inspection.scheduled_date || inspection.created_at;
                    return d ? new Date(d).toLocaleString() : "—";
                  })()}
                </p>
              </div>
            </div>
            
            {inspection.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="text-slate-800">{inspection.location}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {inspection.notes && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="font-semibold text-slate-800 mb-3">Inspection Notes</h2>
            <p className="text-slate-700 whitespace-pre-wrap">{inspection.notes}</p>
          </div>
        )}

        {/* Photos */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="font-semibold text-slate-800 mb-4">
            Inspection Photos ({inspection.photo_count})
          </h2>
          
          {inspection.photos?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {inspection.photos.map((photo, index) => (
                <button
                  key={photo.id || index}
                  onClick={() => openPhotoViewer(index)}
                  className="aspect-square rounded-xl overflow-hidden bg-slate-100 hover:opacity-90 transition-opacity relative group"
                >
                  <img
                    src={photoSrc(photo)}
                    alt={photo.filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-medium">
                      View
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No photos attached</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          <p>This inspection report was shared by Europacific d.o.o.</p>
          <p className="mt-1">
            Questions? Contact us at{" "}
            <a href="mailto:info@europacific.com" className="text-blue-600 hover:underline">
              info@europacific.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
